import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DollarSign, Download, Save, Loader2 } from 'lucide-react';

const RATE_CARDS = [
  { key: 'day_program', label: 'Explore Program', note: 'per unit per participant', color: 'border-[#9B7ED8] bg-[#EDE7F6]/30' },
  { key: 'employment', label: 'Employment', note: 'per unit', color: 'border-[#FFB74D] bg-[#FFF3E0]/30' },
  { key: 'in_home', label: 'IHCS', note: 'per unit', color: 'border-[#81C784] bg-[#E8F5E9]/30' },
];

export default function BillingTab({
  billingRates, setBillingRates, billingSaving, handleSaveBillingRates,
  exportForm, setExportForm, exporting, handleExportBilling,
}) {
  return (
    <div className="space-y-5">
      <Card className="border-[#DED8EB] shadow-sm" data-testid="billing-rates-settings">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <DollarSign className="h-4 w-4 text-[#2E7D32]" /> Billing Rates (per 15-min unit)
          </CardTitle>
          <p className="text-xs text-[#8E85A0]">PA Office of Developmental Programs — Medicaid. Changes apply to dashboard projections immediately.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {RATE_CARDS.map(({ key, label, note, color }) => (
              <div key={key} className={`p-4 rounded-lg border ${color}`}>
                <Label className="text-sm font-semibold text-[#2D1B4E]">{label}</Label>
                <p className="text-[10px] text-[#8E85A0] mb-2">{note}</p>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-bold text-[#5A4D6E]">$</span>
                  <Input type="number" step="0.01" value={billingRates[key] || 0}
                    onChange={e => setBillingRates(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))}
                    className="border-[#DED8EB] bg-white w-28 text-right font-mono" style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                    data-testid={`billing-rate-${key}`} />
                </div>
              </div>
            ))}
          </div>
          <Button onClick={handleSaveBillingRates} disabled={billingSaving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="save-billing-rates">
            {billingSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {billingSaving ? 'Saving...' : 'Save Rates'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-[#DED8EB] shadow-sm" data-testid="billing-export">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Download className="h-4 w-4 text-[#6B3FA0]" /> Billing Export (CSV)
          </CardTitle>
          <p className="text-xs text-[#8E85A0]">Download a Medicaid/ODP-ready CSV of shifts, units, and billable amounts for any date range.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-semibold text-[#2D1B4E]">Start Date</Label>
              <Input type="date" value={exportForm.start_date} onChange={e => setExportForm(p => ({ ...p, start_date: e.target.value }))} className="border-[#DED8EB] bg-white mt-1" data-testid="export-start-date" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#2D1B4E]">End Date</Label>
              <Input type="date" value={exportForm.end_date} onChange={e => setExportForm(p => ({ ...p, end_date: e.target.value }))} className="border-[#DED8EB] bg-white mt-1" data-testid="export-end-date" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#2D1B4E]">Service Line</Label>
              <select value={exportForm.service_line} onChange={e => setExportForm(p => ({ ...p, service_line: e.target.value }))}
                className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm bg-white mt-1" data-testid="export-service-line">
                <option value="">All Services</option>
                <option value="day_program">Explore Program</option>
                <option value="employment">Employment</option>
                <option value="in_home">IHCS</option>
              </select>
            </div>
          </div>
          <Button onClick={handleExportBilling} disabled={exporting} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="export-billing-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? 'Building CSV...' : 'Download CSV'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
